<script setup lang="ts">
    import { computed, onMounted, ref, watch } from 'vue';
    import ModalBase from '@/shared/components/ModalBase.vue';
    import { getLibrary, getScopes } from '@/shared/helpers/api';
    import { codeEditPermissionsForLibrary } from '@/shared/directives/requirePermission';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow';
    import {
        buildCodeSetOptions,
        codeSetNamesForScopes,
        CodeSetSelectorOption,
        filterCodeSetOptions
    } from '@/shared/components/codesetselector/codeSetSelectionHelper';

    type Props = {
        libraryName: string;
        /** The codeset being copied from. It is never offered as a target. */
        sourceCodeSetName: string;
        title: string;
        subtitle?: string;
        confirmLabel?: string;
        /** Scopes that already hold the content being copied. Their codesets stay selectable but are tagged. */
        existingScopes?: string[];
        /** The most codesets that can be selected at once. Above it, confirm is disabled and limitMessage is shown. */
        maxSelected?: number;
        limitMessage?: string;
    };

    const props = withDefaults(defineProps<Props>(), {
        subtitle: '',
        confirmLabel: 'Confirm',
        existingScopes: () => [],
        maxSelected: Number.POSITIVE_INFINITY,
        limitMessage: ''
    });

    const emit = defineEmits<{
        confirm: [codeSetNames: string[]];
        cancel: [];
    }>();

    const { showError } = useShow();

    const loading = ref(true);
    const options = ref<CodeSetSelectorOption[]>([]);
    const searchText = ref('');
    const selectedNames = ref<string[]>([]);

    onMounted(async () => {
        try {
            const library = await getLibrary(http, props.libraryName, {
                includeAccessGroups: false,
                includeAttachment: false,
                includeTags: false,
                includeCodeSets: true
            });
            const scopes = await getScopes(http, library.scopeType);
            let codeSetOptions = buildCodeSetOptions(library.codeSets, scopes, props.sourceCodeSetName);
            if (!library.isGlobal) {
                const permissions = await codeEditPermissionsForLibrary(props.libraryName);
                codeSetOptions = codeSetOptions.filter(option =>
                    option.scopes.length > 0 && option.scopes.every(scope => permissions[scope.identity]));
            }
            options.value = codeSetOptions;
        } catch (error) {
            showError(error);
        } finally {
            loading.value = false;
        }
    });

    const existingCodeSetSet = computed(() => new Set(codeSetNamesForScopes(options.value, props.existingScopes)));

    const hasExisting = computed(() => existingCodeSetSet.value.size > 0);

    const hideExisting = ref(true);

    const visibleOptions = computed(() =>
        hideExisting.value
            ? options.value.filter(option => !isExisting(option))
            : options.value);

    const filteredOptions = computed(() => filterCodeSetOptions(visibleOptions.value, searchText.value));

    // Otherwise a hidden library set could stay selected and be overwritten without being visible in the list
    watch([hideExisting, existingCodeSetSet], ([hide]) => {
        if (hide)
            selectedNames.value = selectedNames.value.filter(name => !existingCodeSetSet.value.has(name));
    });

    const isFiltered = computed(() => filteredOptions.value.length !== options.value.length);

    const selectAllLabel = computed(() => isFiltered.value ? 'Select all shown' : 'Select all');

    const statusText = computed(() =>
        isFiltered.value
            ? `${selectedNames.value.length} selected \u00b7 showing ${filteredOptions.value.length} of ${options.value.length}`
            : `${selectedNames.value.length} of ${options.value.length} selected`);

    function isExisting(option: CodeSetSelectorOption): boolean {
        return existingCodeSetSet.value.has(option.name);
    }

    function secondaryText(option: CodeSetSelectorOption): string {
        return option.scopes.map(scope => scope.description).filter(Boolean).join(', ') || option.description;
    }

    function selectAllFiltered(): void {
        selectedNames.value = [...new Set([...selectedNames.value, ...filteredOptions.value.map(option => option.name)])];
    }

    function clearSelection(): void {
        selectedNames.value = [];
    }

    const overLimit = computed(() => selectedNames.value.length > props.maxSelected);

    function onSubmit(): void {
        if (selectedNames.value.length === 0 || overLimit.value)
            return;

        // Keep the on-screen order rather than click order
        emit('confirm', options.value.map(option => option.name).filter(name => selectedNames.value.includes(name)));
    }
</script>

<template>
    <!-- Teleported so the modal does not inherit styling from the toolbar field it is opened from -->
    <teleport to="body">
        <modal-base
            max-width="800px"
            width="52rem"
            max-height="calc(100vh - 4em)"
            @on-cancel="emit('cancel')">
            <template #modal-header>
                {{ title }}
            </template>
            <template #modal-body>
                <p
                    v-if="subtitle"
                    class="mb-4">
                    {{ subtitle }}
                </p>
                <b-loading
                    :is-full-page="false"
                    :model-value="loading" />
                <b-field>
                    <b-input
                        v-model="searchText"
                        placeholder="Search scopes or library sets..."
                        type="search"
                        icon="magnify"
                        expanded />
                </b-field>

                <b-field v-if="hasExisting">
                    <b-checkbox v-model="hideExisting">
                        Hide library sets that already have this code
                    </b-checkbox>
                </b-field>

                <b-field grouped>
                    <p class="control">
                        <b-button
                            size="is-small"
                            :disabled="filteredOptions.length === 0"
                            @click="selectAllFiltered">
                            {{ selectAllLabel }}
                        </b-button>
                    </p>
                    <p class="control">
                        <b-button
                            size="is-small"
                            :disabled="selectedNames.length === 0"
                            @click="clearSelection">
                            Clear selection
                        </b-button>
                    </p>
                    <p class="control is-flex is-align-items-center has-text-grey">
                        {{ statusText }}
                    </p>
                </b-field>

                <p
                    v-if="overLimit"
                    class="has-text-danger mb-3">
                    {{ limitMessage }}
                </p>

                <div class="code-set-selector-list">
                    <p
                        v-if="!loading && options.length === 0"
                        class="has-text-grey p-3">
                        There are no other library sets you can write to.
                    </p>
                    <p
                        v-else-if="!loading && visibleOptions.length === 0"
                        class="has-text-grey p-3">
                        All writable library sets already have this code. Untick the box above to update them.
                    </p>
                    <p
                        v-else-if="!loading && filteredOptions.length === 0"
                        class="has-text-grey p-3">
                        No library sets match the current filter.
                    </p>
                    <!-- The description line is clamped, so the tooltip carries the full detail -->
                    <b-tooltip
                        v-for="(option, index) in filteredOptions"
                        :key="option.name"
                        class="is-flex"
                        multilined
                        size="is-large"
                        append-to-body
                        :delay="500">
                        <template #content>
                            <div class="has-text-left">
                                <p class="has-text-weight-bold mb-1">
                                    {{ option.description || option.name }}
                                </p>
                                <p
                                    v-for="scope in option.scopes"
                                    :key="scope.identity">
                                    <span class="has-text-weight-bold">{{ scope.identity }}</span> <span class="has-text-weight-normal">{{ scope.description }}</span>
                                </p>
                            </div>
                        </template>
                        <div
                            class="is-flex is-align-items-center px-3 py-2"
                            :class="{ 'has-background-white-bis': index % 2 === 1 }">
                            <b-checkbox
                                v-model="selectedNames"
                                class="is-flex-grow-1 is-align-items-flex-start"
                                :native-value="option.name">
                                <span class="tags mb-1">
                                    <template v-if="option.scopes.length > 0">
                                        <span
                                            v-for="scope in option.scopes"
                                            :key="scope.identity"
                                            class="tag has-text-weight-semibold">{{ scope.identity }}</span>
                                    </template>
                                    <span
                                        v-else
                                        class="has-text-weight-bold">{{ option.name }}</span>
                                </span>
                                <span
                                    v-if="secondaryText(option)"
                                    class="code-set-selector-description is-size-7 has-text-grey">{{ secondaryText(option) }}</span>
                            </b-checkbox>
                            <b-tag
                                v-if="isExisting(option)"
                                type="is-warning is-light"
                                size="is-small">
                                Already exists
                            </b-tag>
                        </div>
                    </b-tooltip>
                </div>
            </template>
            <template #modal-footer>
                <div class="buttons">
                    <b-button
                        label="Cancel"
                        @click="emit('cancel')" />
                    <b-button
                        :label="`${confirmLabel} (${selectedNames.length})`"
                        type="is-primary"
                        :disabled="selectedNames.length === 0 || overLimit"
                        @click="onSubmit" />
                </div>
            </template>
        </modal-base>
    </teleport>
</template>

<style scoped>
/*
    Same layout as the shipping library selector: the modal is capped by max-height,
    and only the codeset list scrolls.
*/
:deep(.modal-card-body) {
    display: flex;
    flex-direction: column;
    min-height: 0;
}

:deep(.modal-card-body > div) {
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1 1 auto;
}

.code-set-selector-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    border: solid 1px var(--bulma-border);
}

/* Bulma has no line-clamp helper; the full text is in the row tooltip */
.code-set-selector-description {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
}
</style>
